import { Navigate } from "react-router-dom";
import type { ReactElement } from "react";
import { useAuth } from "../hooks/useAuth";
import { SessionBootScreen } from "../components/boot/SessionBootScreen";

interface OnboardingGuardProps {
  children: ReactElement;
}

/**
 * Decide se quem está logado precisa passar pelo wizard.
 *
 * ⚠️ BUG-202: `profile === null` NÃO significa "conta nova" — significa que o
 * perfil ainda não foi lido (o `ensureUserProfile` cria o doc no primeiro
 * login, então uma conta logada sem doc não existe por construção). Até
 * 03/09 este guard lia `!profile?.onboardingCompleted` e mandava para o
 * `/onboarding` qualquer sessão cujo perfil falhou em carregar — inclusive a
 * do fundador, concluída desde maio, sempre que o Firestore do navegador
 * morria (`INTERNAL ASSERTION FAILED`). O wizard então tentava gravar a
 * declaração de livros e as rules recusavam, porque `lifetimeBooksRead` só é
 * aceito na transição `onboardingCompleted` false → true.
 *
 * Só o valor EXPLÍCITO decide. Perfil ausente com boot "pronto" é estado que
 * o `AuthContext` não produz mais (vira falha de boot); se aparecer por outra
 * porta, a tela de abertura é a resposta honesta — nunca o wizard.
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { profile, isLoading } = useAuth();

  // Boot em curso: a mesma tela de abertura do `ProtectedRoute` (17/09) — um
  // `null` aqui era um frame em branco entre duas telas de marca.
  if (isLoading) {
    return <SessionBootScreen />;
  }

  if (!profile) {
    return <SessionBootScreen />;
  }

  if (profile.onboardingCompleted !== true) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}
